import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { errorTracker } from '@/lib/error-tracker';

interface ErrorTrackingContextType {
  isInErrorState: boolean;
  consecutiveFailures: number;
  reset: () => void;
}

const ErrorTrackingContext = createContext<ErrorTrackingContextType | undefined>(undefined);

export const ErrorTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInErrorState, setIsInErrorState] = useState(() => errorTracker.isInErrorState());
  const [consecutiveFailures, setConsecutiveFailures] = useState(() => errorTracker.getConsecutiveFailures());

  useEffect(() => {
    // Subscribe to error tracker updates
    const unsubscribe = errorTracker.subscribe((failures) => {
      setConsecutiveFailures(failures);
      setIsInErrorState(errorTracker.isInErrorState());
    });

    // Check periodically for inactivity and update error state
    const inactivityCheckInterval = setInterval(() => {
      const lastActivityTime = errorTracker.getLastActivityTime();
      const timeSinceLastActivity = Date.now() - lastActivityTime;
      const INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes
      const currentFailures = errorTracker.getConsecutiveFailures();
      
      // Show error state if:
      // 1. We have 3+ consecutive failures (all queries failing), OR
      // 2. User has been inactive for 30+ minutes AND we have some failures
      const shouldShowError = 
        currentFailures >= 3 || 
        (timeSinceLastActivity > INACTIVITY_THRESHOLD && currentFailures > 0);
      
      setIsInErrorState(shouldShowError);
    }, 5000); // Check every 5 seconds

    return () => {
      unsubscribe();
      clearInterval(inactivityCheckInterval);
    };
  }, []);

  const reset = useCallback(() => {
    errorTracker.reset();
    setIsInErrorState(false);
    setConsecutiveFailures(0);
  }, []);

  const value: ErrorTrackingContextType = {
    isInErrorState,
    consecutiveFailures,
    reset,
  };

  return (
    <ErrorTrackingContext.Provider value={value}>
      {children}
    </ErrorTrackingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useErrorTrackingContext = () => {
  const context = useContext(ErrorTrackingContext);
  if (context === undefined) {
    throw new Error('useErrorTrackingContext must be used within an ErrorTrackingProvider');
  }
  return context;
};

